import { SearchSnippet } from './search-snippet.js';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Dialog as DialogPrimitive } from 'radix-ui';
import { Command } from 'cmdk';
import { BookOpen, FileText, FolderKanban, GraduationCap, Inbox, LayoutDashboard, ListTodo, Plus, Search } from 'lucide-react';
import { useApiQuery } from '../lib/api.js';
import { useTranslation } from '../lib/i18n.js';
import { KeyLabel, PhaseBadge, RequestStatusBadge } from './badges.js';
import { useDebounced } from '../lib/debounce.js';

/** Keyboard-first search and navigation (/ or mod+K). Results are permission-filtered by the API. */
export function CommandPalette({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const q = useDebounced(query.trim());
  const enabled = open && q.length >= 2;
  const work = useApiQuery('listWorkItems', { query: { q, limit: '6', includeCanceled: 'true' } }, { enabled });
  const requests = useApiQuery('listRequests', { query: { q, limit: '6', view: 'all' } }, { enabled });
  // Documents match by title, filtered by folder access and locks like everything else.
  const documents = useApiQuery('searchDocuments', { query: { q, limit: '6' } }, { enabled });

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const go = (path: string) => {
    onOpenChange(false);
    navigate(path);
  };

  const keyMatch = /^(VAM|REQ)-\d+$/i.test(q) ? q.toUpperCase() : null;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/30" />
        <DialogPrimitive.Content className="fixed top-[12vh] left-1/2 z-50 w-[min(640px,calc(100vw-2rem))] -translate-x-1/2 overflow-hidden rounded-[var(--radius-lg)] border border-border bg-surface shadow-2xl" aria-describedby={undefined}>
          <DialogPrimitive.Title className="sr-only">{t('nav.search')}</DialogPrimitive.Title>
          <Command shouldFilter={false} loop label={t('nav.search')}>
            <div className="flex items-center gap-2 border-b border-border px-3">
              <Search className="size-4 text-fg-subtle" />
              <Command.Input autoFocus value={query} onValueChange={setQuery} placeholder={t('nav.searchPlaceholder')} className="h-11 w-full bg-transparent text-sm outline-none placeholder:text-fg-subtle" />
            </div>
            <Command.List className="max-h-[60vh] overflow-y-auto p-1.5">
              <Command.Empty className="px-3 py-6 text-center text-sm text-fg-subtle">{t('common.noResults')}</Command.Empty>
              {keyMatch ? (
                <Command.Group heading={t('nav.search')} className="text-xs text-fg-subtle [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1">
                  <Item onSelect={() => go(`/go/${keyMatch}`)} icon={<FileText />} label={keyMatch} />
                </Command.Group>
              ) : null}
              {(work.data?.data.length ?? 0) > 0 ? (
                <Command.Group heading={t('nav.work')} className="text-xs text-fg-subtle [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1">
                  {work.data!.data.map((w) => (
                    <Item key={w.id} onSelect={() => go(`/work/${w.key}`)} icon={<ListTodo />} label={w.title} meta={<><KeyLabel value={w.key} /><PhaseBadge phase={w.phase} /></>} />
                  ))}
                </Command.Group>
              ) : null}
              {(requests.data?.data.length ?? 0) > 0 ? (
                <Command.Group heading={t('nav.requests')} className="text-xs text-fg-subtle [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1">
                  {requests.data!.data.map((r) => (
                    <Item key={r.id} onSelect={() => go(`/requests/${r.key}`)} icon={<Inbox />} label={r.title || '—'} meta={<><KeyLabel value={r.key} /><RequestStatusBadge status={r.status} /></>} />
                  ))}
                </Command.Group>
              ) : null}
              {(documents.data?.data.length ?? 0) > 0 ? (
                <Command.Group heading={t('nav.documents')} className="text-xs text-fg-subtle [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1">
                  {documents.data!.data.map((d) => (
                    <Item
                      key={d.id}
                      onSelect={() => go(`/docs/${d.id}`)}
                      icon={d.icon ? <span aria-hidden className="text-sm leading-none">{d.icon}</span> : <FileText />}
                      label={d.title}
                      detail={d.snippet ? <SearchSnippet text={d.snippet} /> : undefined}
                      meta={<span className="max-w-48 truncate text-xs text-fg-subtle">{d.path}</span>}
                    />
                  ))}
                </Command.Group>
              ) : null}
              {!q ? (
                <Command.Group heading={t('common.all')} className="text-xs text-fg-subtle [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1">
                  <Item onSelect={() => go('/requests/new')} icon={<Plus />} label={t('nav.newRequest')} meta={<kbd className="text-[11px]">R</kbd>} />
                  <Item onSelect={() => go('/work?create=1')} icon={<Plus />} label={t('nav.newWork')} meta={<kbd className="text-[11px]">C</kbd>} />
                  <Item onSelect={() => go('/my-work')} icon={<LayoutDashboard />} label={t('nav.myWork')} />
                  <Item onSelect={() => go('/requests')} icon={<Inbox />} label={t('nav.requests')} />
                  <Item onSelect={() => go('/work')} icon={<ListTodo />} label={t('nav.work')} />
                  <Item onSelect={() => go('/projects')} icon={<FolderKanban />} label={t('nav.projects')} />
                  <Item onSelect={() => go('/docs')} icon={<BookOpen />} label={t('nav.documents')} />
                  <Item onSelect={() => go('/tutorial')} icon={<GraduationCap />} label={t('nav.tutorial')} />
                </Command.Group>
              ) : null}
            </Command.List>
          </Command>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function Item({ onSelect, icon, label, detail, meta }: { onSelect: () => void; icon: React.ReactNode; label: string; detail?: React.ReactNode; meta?: React.ReactNode }) {
  return (
    <Command.Item onSelect={onSelect} className="flex cursor-pointer items-center gap-2.5 rounded-[var(--radius-sm)] px-2 py-2 text-sm text-fg data-[selected=true]:bg-muted [&_svg]:size-4 [&_svg]:text-fg-subtle">
      {icon}
      <span className="min-w-0 flex-1">
        <span className="block truncate">{label}</span>
        {detail ? <span className="block truncate text-xs text-fg-muted">{detail}</span> : null}
      </span>
      {meta ? <span className="flex shrink-0 items-center gap-2">{meta}</span> : null}
    </Command.Item>
  );
}
